/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { CardListSkeleton, DataState, DataStateError } from '@/shared/ui';
import { DealsService, type Deal } from '@/services/requests/housien-services/DealsService';
import { useAuth } from '@context/authentication';
import { VisitDrawer } from '@/features/visits/components/VisitDrawer';
import { useAgencyProperties } from '@/features/visits/hooks/useAgencyProperties';
import { useAgency } from '@pages/authenticated/agency-pages/AgenciesContext';
import { DealActionBar, DealStoppedActions } from '../components/DealActionBar';
import { MoveStageSheet, MobileDetailHeader } from '@/shared/ui/components/composed/mobile-sheet';
import { useIsMobile } from '@/shared/hooks/use-is-mobile';
import {
  DealDetailBreadcrumb,
  DealDetailHeader,
} from '../components/DealDetailHeader';
import { DealClientCard } from '../components/DealClientCard';
import { DealDetailTabs, isChecklistTabAvailable, type DealDetailTab } from '../components/DealDetailTabs';
import { DealDocumentsInline } from '../components/DealDocumentsInline';
import { DealClosingPanel } from '../components/DealClosingPanel';
import { DealContactsPanel } from '../components/DealContactsPanel';
import { DealSummaryPanel, DealWantsPanel } from '../components/DealSummaryPanel';
import { DealOutcomeCard } from '../components/DealOutcomeCard';
import { DealProposalsPanel } from '../components/DealProposalsPanel';
import { isConditionallyAccepted } from '../components/ProposalDetails';
import type { DealProposal } from '@/services/requests/housien-services/DealsService';
import { DealSummaryCards } from '../components/DealSummaryCards';
import { DealTimelineSidebar } from '../components/DealTimelineSidebar';
import { CreateDealDialog, type CreateDealFormData } from '../components/CreateDealDialog';
import { DefinePropertyDialog } from '../components/DefinePropertyDialog';
import { EditAttendanceDialog } from '../components/EditAttendanceDialog';
import { LoseDealDialog } from '../components/LoseDealDialog';
import { RecordProposalDialog } from '../components/RecordProposalDialog';
import { RecordContactDialog } from '../components/RecordContactDialog';
import { RENTAL_CLOSING_CHECKLIST, SALE_CLOSING_CHECKLIST } from '../constants/closing-checklist';
import { reportError } from '@/shared/ui/components/basic/error-notification/report-error';
import { useDealDetail } from '../hooks/useDealDetail';
import { useDealDetailDialogs, type DealDialog } from '../hooks/useDealDetailDialogs';
import { useDealPartyNames, type DealPartyNames } from '../hooks/useDealPartyNames';
import { notifications } from '@/shared/ui/components/basic/Notifications';
import { useDealVisits } from '../hooks/useDealVisits';
import { DealVisitsPanel } from '../components/DealVisitsPanel';
import { VisitFeedbackDialog } from '../components/VisitFeedbackDialog';
import { isVisitInFuture } from '@features/visits/utils/visit-timing';
import type { Visit } from '@/services/requests/housien-models/Visit';
import { canReassignBroker } from '../utils/deal-permissions';
import { dealViewState, type DealViewState } from '../utils/deal-state';
import { getCurrentUserRole } from '@/shared/utils/permissions';

/** The capture channels the backend can emit (`ClientOrigin.VALID_CHANNELS`). */
const KNOWN_ORIGIN_CHANNELS: readonly string[] = [
  'site', 'portal_olx', 'portal_grupo_olx', 'meta', 'referral', 'manual',
];

/** A checklist state, either the deal's own or a just-toggled override. */
type Checklist = Record<string, boolean> | null | undefined;

/**
 * Progress on the closing checklist for the deal's own type.
 *
 * Counted item by item against that type's list rather than by the size
 * of the stored map: a deal keeps the other type's ids after a switch,
 * and cardinality alone read those stale ids as progress.
 *
 * @param deal - The deal whose type selects the list.
 * @param override - A checklist the user just toggled, if any.
 * @returns Done and total counts.
 */
function checklistProgressOf(deal: Deal, override?: Checklist): { done: number; total: number } {
  const items = deal.deal_type === 'sale' ? SALE_CLOSING_CHECKLIST : RENTAL_CLOSING_CHECKLIST;
  const checklist = override ?? deal.closing_checklist ?? {};
  return {
    done: items.filter((item) => checklist[item.id]).length,
    total: items.length,
  };
}

/**
 * Localized label for the deal's capture channel. An unrecognized
 * channel resolves to a generic key rather than the raw wire value.
 */
function useOriginLabel(deal: Deal): string {
  const { t } = useTranslation('features/deals');
  const channel = deal.origin?.channel;
  const known = channel && KNOWN_ORIGIN_CHANNELS.includes(channel);
  return t(`originChannels.${known ? channel : 'unknown'}`);
}

interface TabPanelProps {
  agencyId: string;
  deal: Deal;
  view: DealViewState;
  names: DealPartyNames;
  tab: DealDetailTab;
  detail: ReturnType<typeof useDealDetail>;
  onChecklistChange: (checklist: Record<string, boolean>) => void;
  onRecordProposal: () => void;
  onCounterProposal: (proposal: DealProposal) => void;
  /** The attendance's visits, read once by the body and passed down. */
  visits: ReturnType<typeof useDealVisits>;
  /** Settles one visit from its own row. */
  onSettleVisit: (visit: Visit, status: 'confirmed' | 'done') => void;
  /** Opens the booking drawer from the visits section. */
  onScheduleVisit: () => void;
  onRecordContact: () => void;
}

/**
 * The panel below the tab strip, one per tab.
 *
 * @param props - The deal, its view state and names, the active tab, and
 *   the detail controller for the document actions.
 * @returns The active tab's panel.
 */
/**
 * Opens the proposal form, or says why it cannot be opened.
 *
 * The refusal existed already, but only after the form was submitted: a
 * broker filled in value, terms and conditions before being told the
 * attendance had no property to make an offer on. The check is cheap and
 * the answer is known before the first keystroke.
 *
 * Raises the same copy the server's refusal raises, so the message a
 * broker sees cannot drift depending on how far they got.
 *
 * @param deal - The attendance the offer would belong to.
 * @param open - Opens the dialog when the attendance can carry an offer.
 * @returns The guarded opener.
 */
function useProposalGate(deal: Deal, open: () => void): () => void {
  const { t } = useTranslation('features/deals');
  return useCallback(() => {
    if (!deal.property_id) {
      reportError(undefined, {
        title: t('detail.errors.propertyRequiredTitle'),
        message: t('detail.errors.propertyRequiredMessage'),
      });
      return;
    }
    open();
  }, [deal.property_id, open, t]);
}

/**
 * DealTabPanel component.
 */
function DealTabPanel({
  agencyId, deal, view, names, tab, detail, onChecklistChange, onRecordProposal,
  onCounterProposal, onRecordContact, visits, onSettleVisit, onScheduleVisit,
}: TabPanelProps) {
  const originLabel = useOriginLabel(deal);

  if (tab === 'proposals') {
    return (
      <DealProposalsPanel
        proposals={deal.proposals ?? []}
        frozen={(deal.proposals ?? []).some(
          (p) => p.status === 'accepted' && !isConditionallyAccepted(p),
        )}
        canRecord={view.lifecycle === 'running'}
        hasProperty={Boolean(deal.property_id)}
        onRecord={onRecordProposal}
        onAnswer={(proposalId, status) => { void detail.actions.answerProposal(proposalId, status); }}
        onDelete={(proposalId) => { void detail.actions.deleteProposal(proposalId); }}
        onCounter={onCounterProposal}
        onSettleCondition={(proposalId, conditionId, state) => {
          void detail.actions.settleCondition(proposalId, conditionId, state);
        }}
      />
    );
  }
  if (tab === 'documents') {
    return (
      <DealDocumentsInline
        documents={detail.documents}
        loading={detail.docsLoading}
        error={detail.docsError}
        onUpload={detail.actions.uploadDocument}
        onDelete={detail.actions.deleteDocument}
        onRename={detail.actions.renameDocument}
      />
    );
  }
  if (tab === 'contacts') {
    return (
      <DealContactsPanel
        events={deal.events}
        onRecordContact={view.lifecycle === 'running' ? onRecordContact : undefined}
      />
    );
  }
  if (tab === 'checklist') {
    return (
      <DealClosingPanel
        agencyId={agencyId}
        dealId={deal.deal_id}
        dealType={deal.deal_type === 'sale' ? 'sale' : 'rental'}
        initialChecklist={deal.closing_checklist}
        onUpload={detail.actions.uploadDocument}
        onChecklistChange={onChecklistChange}
      />
    );
  }
  return (
    <div className="flex flex-col gap-(--space-4)">
      {/* First, not above the tabs: a finished attendance's outcome — the
          accepted value, the Venda or Locação it opened, the reason it
          was lost — is a fact about the attendance, so it belongs with
          the other facts rather than as a banner over all five tabs. */}
      {view.lifecycle === 'terminal' && (
        <DealOutcomeCard agencyId={agencyId} deal={deal} view={view} />
      )}
      <DealSummaryPanel
        agencyId={agencyId}
        deal={deal}
        names={names}
        originLabel={originLabel}
      />
      <DealWantsPanel agencyId={agencyId} deal={deal} />
      <DealVisitsPanel
        agencyId={agencyId}
        visits={visits.visits}
        isLoading={visits.isLoading}
        onSettle={onSettleVisit}
        onSchedule={onScheduleVisit}
      />
    </div>
  );
}

/**
 * The properties an offer or a closing may be recorded against.
 *
 * Normally the attendance's own list, labelled and in its own order,
 * because that order is what says which one is the primary. An
 * attendance carrying none falls back to the agency's whole catalogue
 * with nothing preselected: a win needs a property, and with an empty
 * list the picker was not drawn at all, so the closing could not be
 * recorded and the form never said why.
 *
 * @param agencyId - The owning agency.
 * @param deal - The attendance whose interest list is being labelled.
 * @returns The options, and whether the first one is the attendance's own.
 */
function useProposalPropertyOptions(agencyId: string, deal: Deal) {
  const { properties } = useAgencyProperties(agencyId);
  return useMemo(() => {
    const ids = deal.property_ids?.length
      ? deal.property_ids
      : (deal.property_id ? [deal.property_id] : []);
    if (!ids.length) {
      return {
        options: properties.map((p) => ({ id: p.id, label: p.label })),
        preselect: false,
      };
    }
    const labels = new Map(properties.map((p) => [p.id, p.label]));
    return {
      options: ids.map((id) => ({ id, label: labels.get(id) ?? id })),
      preselect: true,
    };
  }, [properties, deal.property_ids, deal.property_id]);
}

interface DialogsProps {
  agencyId: string;
  deal: Deal;
  dialog: DealDialog;
  /** Set when the proposal dialog was opened to answer an offer. */
  counterTo?: DealProposal;
  close: () => void;
  detail: ReturnType<typeof useDealDetail>;
  onCreateDeal: (data: CreateDealFormData) => Promise<void>;
}

/**
 * Every modal the screen can open, mounted once beside the layout.
 *
 * @param props - The open dialog, its close callback, and the deal
 *   context each form needs.
 * @returns The dialog set.
 */
function DealDetailDialogs({
  agencyId, deal, dialog, counterTo, close, detail, onCreateDeal,
}: DialogsProps) {
  const { user } = useAuth();
  const agency = useAgency((state) => state.agency);
  const { actions } = detail;
  const proposalProperties = useProposalPropertyOptions(agencyId, deal);

  return (
    <>
      <RecordProposalDialog
        open={dialog === 'proposal'}
        onClose={close}
        counterTo={counterTo}
        properties={proposalProperties.options}
        preselectProperty={proposalProperties.preselect}
        // The attendance's own type when it has one; the declared
        // interest is only a starting guess for an undecided one.
        // Narrowed here rather than on `Deal`, where the field is a
        // bare string: an attendance may legitimately carry none.
        lockedDealType={
          deal.deal_type === 'sale' || deal.deal_type === 'rental'
            ? deal.deal_type
            : null
        }
        defaultDealType={deal.interest === 'rent' ? 'rental' : 'sale'}
        onSubmit={async (value, body) => {
          if (counterTo) {
            await actions.counterProposal(counterTo.proposal_id, {
              value, side: 'owner',
              rental_terms: body.rental_terms, sale_terms: body.sale_terms,
            });
            return;
          }
          await actions.recordProposal(value, body);
        }}
      />
      {/*
        Its own instance rather than a mode toggled on the one above:
        the two are never open together, and sharing one would leave the
        close form pre-filled with whatever a half-finished offer had.
      */}
      <RecordProposalDialog
        open={dialog === 'win'}
        onClose={close}
        mode="win"
        properties={proposalProperties.options}
        preselectProperty={proposalProperties.preselect}
        lockedDealType={
          deal.deal_type === 'sale' || deal.deal_type === 'rental'
            ? deal.deal_type
            : null
        }
        defaultDealType={deal.interest === 'rent' ? 'rental' : 'sale'}
        onSubmit={async (value, body) => {
          await actions.recordAcceptedProposal(value, body);
        }}
      />
      <RecordContactDialog
        open={dialog === 'contact'}
        onClose={close}
        onSubmit={async (note) => { await actions.recordContact(note); }}
      />
      <LoseDealDialog
        open={dialog === 'lose'}
        onClose={close}
        onSubmit={async (reason, note) => { await actions.lose(reason, note); }}
      />
      <DefinePropertyDialog
        open={dialog === 'property'}
        onClose={close}
        agencyId={agencyId}
        initialPropertyId={deal.property_id ?? null}
        onSubmit={async (propertyId) => { await actions.setProperty(propertyId); }}
      />
      <EditAttendanceDialog
        open={dialog === 'edit'}
        onClose={close}
        agencyId={agencyId}
        deal={deal}
        brokers={(agency?.members ?? []).map((m) => ({
          id: m.email,
          label: `${m.first_name} ${m.last_name}`.trim() || m.email,
          sublabel: m.email,
        }))}
        canReassign={canReassignBroker(
          deal.broker_id,
          user?.profile?.email,
          getCurrentUserRole(agency, user?.profile?.email),
        )}
        onSubmit={async (fields) => { await actions.edit(fields); }}
      />
      <VisitDrawer
        open={dialog === 'visit'}
        onClose={close}
        agencyId={agencyId}
        userEmail={user?.profile?.email ?? ''}
        members={agency?.members ?? []}
        initialClientId={deal.client_id}
        initialDealId={deal.deal_id}
        initialPropertyId={deal.property_id}
        onSuccess={() => { void detail.fetchDeal(); }}
        key={deal.deal_id}
      />
      <CreateDealDialog
        open={dialog === 'newAttendance'}
        onClose={close}
        agencyId={agencyId}
        initialClientId={deal.client_id ?? null}
        onSubmit={onCreateDeal}
      />
    </>
  );
}

interface BodyProps {
  agencyId: string;
  deal: Deal;
  detail: ReturnType<typeof useDealDetail>;
  tab: DealDetailTab;
  onTabChange: (tab: DealDetailTab) => void;
  dialogs: ReturnType<typeof useDealDetailDialogs>;
  onCreateDeal: (data: CreateDealFormData) => Promise<void>;
  onBack: () => void;
}

/**
 * The left column: the state-specific block (outcome card or action
 * bar), then the tab strip and its panel.
 */
function DealMainColumn(props: BodyProps & {
  view: DealViewState;
  names: DealPartyNames;
  isMobile: boolean;
  progress: { done: number; total: number };
  onChecklistChange: (checklist: Record<string, boolean>) => void;
  visits: ReturnType<typeof useDealVisits>;
  onSettleVisit: (visit: Visit, status: 'confirmed' | 'done') => void;
  onScheduleVisit: () => void;
}) {
  const { agencyId, deal, detail, view, names, dialogs, progress } = props;
  const openProposal = useProposalGate(deal, () => dialogs.open('proposal'));
  // A deal rolled back out of negotiation drops the checklist tab from
  // under a reader sitting on it; fall back rather than render nothing.
  const tab = isChecklistTabAvailable(view) || props.tab !== 'checklist' ? props.tab : 'summary';

  return (
    <div className="flex min-w-0 flex-col gap-(--space-4) [&>*:last-child]:grow [&>*:last-child]:shrink-0">
      {/*
        On a phone the tabs are rendered by the body, above this grid: a
        sticky element only travels inside its own containing block, and this
        column is 674px of a 1912px scroll — pinned here, the tabs started
        sliding away two thirds of the way down.
      */}
      {!props.isMobile && (
        <DealDetailTabs
          active={tab}
          onChange={props.onTabChange}
          view={view}
          proposalCount={(deal.proposals ?? []).length}
          documentCount={detail.documents.length}
          contactCount={deal.events.filter((event) => event.type === 'contact_recorded').length}
          checklistDone={progress.done}
          checklistTotal={progress.total}
        />
      )}

      <DealTabPanel
        visits={props.visits}
        onSettleVisit={props.onSettleVisit}
        onScheduleVisit={props.onScheduleVisit}
        agencyId={agencyId}
        deal={deal}
        view={view}
        names={names}
        tab={tab}
        detail={detail}
        onChecklistChange={props.onChecklistChange}
        onRecordProposal={openProposal}
        onCounterProposal={dialogs.openCounter}
        onRecordContact={() => dialogs.open('contact')}
      />
    </div>
  );
}

/**
 * The loaded screen: the paused banner when held, the header, the three
 * summary tiles, and the two-column body with the timeline beside it.
 *
 * @param props - The deal, its detail controller, and the tab/dialog state.
 * @returns The screen body.
 */
function DealDetailBody(props: BodyProps) {
  const { agencyId, deal, detail, dialogs, onBack } = props;
  const openProposal = useProposalGate(deal, () => dialogs.open('proposal'));

  const view = dealViewState(deal);
  const names = useDealPartyNames(agencyId, deal);
  const isMobile = useIsMobile();
  const { t } = useTranslation('features/deals');
  /* The phone's "Andamento" sheet; see `DealActionBar`'s `onProgress`. */
  const [progressOpen, setProgressOpen] = useState(false);
  // The screen could book a visit and never read one back, so the action
  // bar had to guess from the stage -- which cannot tell a booking
  // waiting to be confirmed from one that already happened.
  const dealVisits = useDealVisits(agencyId, deal.deal_id, deal.client_id);
  // Holds the visit being recorded as done: the bar reaches only the
  // soonest outstanding one, while a row can settle any of them.
  const [visitFeedbackFor, setVisitFeedbackFor] = useState<Visit | null>(null);
  // Seeded from the deal and replaced on every toggle, so the tile and
  // the tab badge track the boxes without a round trip. Reset whenever a
  // freshly loaded deal arrives.
  const [checklist, setChecklist] = useState<Checklist>(undefined);
  useEffect(() => { setChecklist(undefined); }, [deal.updated_at]);
  const progress = checklistProgressOf(deal, checklist);

  const actions = view.lifecycle === 'running' ? (
    <DealActionBar
      view={view}
      canWin={Boolean(deal.property_id)}
      onRecordContact={() => dialogs.open('contact')}
      onScheduleVisit={() => dialogs.open('visit')}
      visitAction={dealVisits.next.action}
      onSettleVisit={() => {
        if (dealVisits.next.action === 'schedule') return;
        void dealVisits.settle(dealVisits.next.action);
      }}
      onRecordProposal={openProposal}
      onOpenChecklist={() => props.onTabChange('checklist')}
      onMarkWon={() => dialogs.open('win')}
      onPause={detail.actions.pause}
      onLose={() => dialogs.open('lose')}
      onProgress={isMobile ? () => setProgressOpen(true) : undefined}
    />
  ) : (
    <DealStoppedActions
      view={view}
      onNewAttendance={() => dialogs.open('newAttendance')}
      onResume={detail.actions.resume}
    />
  );

  return (
    <div className="flex grow flex-col gap-(--space-4)">
      {/* The header lives above the scrolling box, in the page shell. */}
      {!isMobile && <DealDetailBreadcrumb deal={deal} names={names} onBack={onBack} />}

      <DealDetailHeader
        agencyId={agencyId}
        deal={deal}
        names={names}
        view={view}
        isRunning={view.lifecycle === 'running'}
        onDefineProperty={() => dialogs.open('property')}
        onEdit={() => dialogs.open('edit')}
      />

      <DealSummaryCards
        agencyId={agencyId}
        deal={deal}
        view={view}
        onRecordProposal={openProposal}
        actions={isMobile ? undefined : actions}
      />


      {/*
        The same sheet the attendances board uses, with no stages in it: the
        detail screen derives its stage from recorded facts and nothing here
        is allowed to push it (see `useDealDetail`). What it carries are the
        three decisions, which are not positions in the funnel.
      */}
      <MoveStageSheet
        open={progressOpen}
        title={t('detail.actions.progress')}
        subjectName={names.clientName ?? ''}
        subjectDetail={names.propertyName ?? undefined}
        options={[]}
        currentId=""
        actions={[
          ...(deal.property_id ? [{
            id: 'won',
            label: t('detail.actions.markWon'),
            tone: 'var(--status-success-text)',
            onSelect: () => { setProgressOpen(false); dialogs.open('win'); },
          }] : []),
          {
            id: 'paused',
            label: t('detail.actions.pause'),
            tone: 'var(--text-secondary)',
            onSelect: () => { setProgressOpen(false); detail.actions.pause(); },
          },
          {
            id: 'lost',
            label: t('detail.actions.lose'),
            tone: 'var(--status-error-text)',
            onSelect: () => { setProgressOpen(false); dialogs.open('lose'); },
          },
        ]}
        onSelect={() => setProgressOpen(false)}
        onClose={() => setProgressOpen(false)}
      />

      {isMobile && (
        <DealDetailTabs
          active={isChecklistTabAvailable(view) || props.tab !== 'checklist' ? props.tab : 'summary'}
          onChange={props.onTabChange}
          view={view}
          proposalCount={(deal.proposals ?? []).length}
          documentCount={detail.documents.length}
          contactCount={deal.events.filter((event) => event.type === 'contact_recorded').length}
          checklistDone={progress.done}
          checklistTotal={progress.total}
        />
      )}

      <div className="grid grow grid-cols-[minmax(0,1fr)_minmax(280px,360px)] items-start gap-(--space-5) max-[1100px]:grid-cols-1">
        <DealMainColumn
          {...props}
          view={view}
          names={names}
          isMobile={isMobile}
          progress={progress}
          onChecklistChange={setChecklist}
          visits={dealVisits}
          onScheduleVisit={() => dialogs.open('visit')}
          onSettleVisit={(visit, status) => {
            if (status === 'confirmed') {
              void dealVisits.settleOne(visit, 'confirmed');
              return;
            }
            // The Visitas board has always refused to record a visit that
            // has not happened; the attendance must not be a way around it.
            if (isVisitInFuture(visit.visit_datetime)) {
              notifications.show({ color: 'red', message: t('detail.visits.notYet') });
              return;
            }
            setVisitFeedbackFor(visit);
          }}
        />
        <div className="flex flex-col gap-(--space-4)">
          <DealClientCard agencyId={agencyId} clientId={deal.client_id} />
          <DealTimelineSidebar
            events={deal.events}
            onRecordContact={() => dialogs.open('contact')}
          />
        </div>
      </div>

      <DealDetailDialogs
        agencyId={agencyId}
        deal={deal}
        dialog={dialogs.dialog}
        counterTo={dialogs.counterTo}
        close={dialogs.close}
        detail={detail}
        onCreateDeal={props.onCreateDeal}
      />
      {/*
        On a phone the actions leave the band and stand at the foot of the
        screen. Inside the band they sat wherever the band happened to be —
        measured at 256px from the bottom — so the one thing the screen is
        for was somewhere in the middle of a scroll.

        Sticky rather than a flex sibling because this component is the
        scrolling box's content, not its parent. Its own root spans the whole
        scroll height, so unlike the tab bar's short grid column there is
        somewhere for the bar to travel.
      */}
      {isMobile && (
        <div
          data-testid="deal-mobile-action-bar"
          className="sticky z-30 -mx-(--space-5) -mb-(--space-5) mt-auto border-t border-[var(--border-subtle)] px-(--space-4) py-(--space-3)"
          /*
           * A negative `bottom` so the bar rests against the true edge. The
           * scrolling box is padded, and sticky pins to its content edge, so
           * `bottom: 0` left the bar floating one padding above the screen —
           * a bar with the page showing under it reads as unanchored.
           */
          style={{ background: 'var(--surface-primary)', bottom: 'calc(-1 * var(--space-5))' }}
        >
          {actions}
        </div>
      )}

    </div>
  );
}

/**
 * Deal ("atendimento") detail screen.
 *
 * The layout is one shape with five faces, keyed off the deal's stage: a
 * running deal gets the action bar, a paused one swaps it for the resume
 * banner, and a terminal one drops it for the read-only outcome card.
 * The summary tiles and the tabbed body stay put throughout, so the
 * screen never rearranges itself under the reader.
 *
 * @returns The deal detail page.
 */
export function DealDetailPage() {
  const { agency_id: agencyId, deal_id: dealId } = useParams<{
    agency_id: string;
    deal_id: string;
  }>();
  const navigate = useNavigate();
  const detail = useDealDetail({ agencyId, dealId });
  const dialogs = useDealDetailDialogs();
  const [tab, setTab] = useState<DealDetailTab>('summary');
  const { deal } = detail;
  const isMobile = useIsMobile();
  const { t: tDeals } = useTranslation('features/deals');
  const names = useDealPartyNames(agencyId ?? '', deal);
  /* The client's name is what a person came here about; the id is not. */
  const headerName = names.clientName ?? tDeals('detail.breadcrumb');
  /* Drives the detail header's shadow; see `MobileDetailHeader`. */
  const [scrolled, setScrolled] = useState(false);

  /*
    The board cannot close a deal itself -- a win needs an accepted offer
    with a value in it -- so dropping a card on "Fechou negócio" sends
    the broker here carrying the intent in the URL. Opened once, then the
    parameter is dropped: left in place, a refresh or a back-navigation
    would reopen the form over an attendance already won.
  */
  const [searchParams, setSearchParams] = useSearchParams();
  const wantsWin = searchParams.get('win') === '1';
  const openDialog = dialogs.open;
  useEffect(() => {
    if (!wantsWin || !deal) return;
    openDialog('win');
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.delete('win');
      return next;
    }, { replace: true });
  }, [wantsWin, deal, openDialog, setSearchParams]);

  const handleBack = useCallback(() => {
    navigate(`/app/agencies/${agencyId}/deals`);
  }, [navigate, agencyId]);

  const handleCreateDeal = useCallback(async (data: CreateDealFormData) => {
    if (!agencyId) return;
    const created = await DealsService.createDeal(agencyId, {
      interest: data.interest,
      broker_id: deal?.broker_id ?? '',
      ...(data.client_id ? { client_id: data.client_id } : {}),
      ...(data.property_id ? { property_id: data.property_id } : {}),
      ...(data.notes ? { notes: data.notes } : {}),
    });
    navigate(`/app/agencies/${agencyId}/deals/${created.deal_id}`);
  }, [agencyId, deal?.broker_id, navigate]);

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden bg-(--surface-primary) font-[family-name:var(--font-family)]">
      {/*
        Above the scrolling box rather than inside it, the same slot the
        listing and client sheets use. Sticky inside the scroller was the
        first attempt and never engaged; a `flex: none` sibling cannot fail
        to stay put, because staying put is all it does.
      */}
      {isMobile && (
        <MobileDetailHeader
          title={headerName}
          onBack={handleBack}
          backLabel={tDeals('detail.breadcrumb')}
          scrolled={scrolled}
        />
      )}
      <div
        className="flex min-h-0 flex-1 flex-col gap-(--space-4) overflow-y-auto p-(--space-5)"
        onScroll={(e) => {
          const next = e.currentTarget.scrollTop > 4;
          setScrolled((current) => (current === next ? current : next));
        }}
      >
      <DataState
        isLoading={detail.loading}
        isError={Boolean(detail.error)}
        isEmpty={!deal}
        loadingFallback={<CardListSkeleton count={3} />}
        errorState={<DataStateError message={detail.error ?? ''} onRetry={detail.fetchDeal} />}
      >
        {deal && agencyId && (
          <DealDetailBody
            agencyId={agencyId}
            deal={deal}
            detail={detail}
            tab={tab}
            onTabChange={setTab}
            dialogs={dialogs}
            onCreateDeal={handleCreateDeal}
            onBack={handleBack}
          />
        )}
      </DataState>
      </div>
    </div>
  );
}
